import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { CountryWithFlag, subscriptionStatusColor } from './AccountUtils';

import {
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Box
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';

const AccountsListTable = ({ accounts, onFilterChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('planStatus') || 'all');
  let navigate = useNavigate();

  useEffect(() => {
    const planParam = searchParams.get('planStatus');
    if (planParam) {
      setStatusFilter(planParam);
    }
  }, [searchParams]);

  const handleStatusFilterChange = (newValue) => {
    setStatusFilter(newValue);
    if (newValue === 'all') {
      searchParams.delete('planStatus');
    } else {
      searchParams.set('planStatus', newValue);
    }
    setSearchParams(searchParams);
    onFilterChange(newValue);
  };

  function goToAccountProfile(id) {
    navigate('/accounts/' + id);
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="Accounts list">
        <TableHead>
          <TableRow>
            <TableCell width="5%">#</TableCell>
            <TableCell width="8%"></TableCell>
            <TableCell width="40%">Name / Email</TableCell>
            <TableCell width="15%">Country</TableCell>
            <TableCell width="32%">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Plan
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{ height: 32 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="canceled">Canceled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts && accounts.length > 0 ? (
            accounts.map((account) => (
              <TableRow
                hover
                key={account.id}
                onClick={() => goToAccountProfile(account.id)}
                sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row" width="5%">
                  {account.id}
                </TableCell>
                <TableCell width="8%">
                  <Avatar alt={account.firstName + ' ' + account.lastName} src={account.profilePicture} />
                </TableCell>
                <TableCell width="40%">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {account.firstName + ' ' + account.lastName}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {account.email}
                      <Tooltip title="Copy email">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(account.email);
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell width="15%">
                  <CountryWithFlag value={account.country || account.signupIpCountry} />
                </TableCell>
                <TableCell width="32%">
                  {account.accountPlan?.plan?.name}{' - '}
                  {account.accountPlan?.plan?.price > 0 && ` $${account.accountPlan.plan.price}`}{' '}
                  <Chip
                    sx={{
                      visibility:
                        account.accountPlan?.subscriptionStatus === 'active' ||
                        account.accountPlan?.subscriptionStatus === 'canceled' ||
                        account.accountPlan?.subscriptionStatus === 'unpaid'
                          ? 'visible'
                          : 'hidden'
                    }}
                    label={account.accountPlan?.subscriptionStatus}
                    color={subscriptionStatusColor(account.accountPlan?.subscriptionStatus)}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No accounts found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

AccountsListTable.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string.isRequired,
      profilePicture: PropTypes.string,
      completedProfile: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      stripeAccountId: PropTypes.string,
      country: PropTypes.string,
      signupIpCountry: PropTypes.string
    })
  ).isRequired,
  onFilterChange: PropTypes.func.isRequired
};

export default AccountsListTable;
